import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/**
 * How the app is lit.
 *
 * The palette lives in styles.css as two sets of CSS variables, dark on
 * `:root` and light under `[data-theme='light']`, and every Tailwind color
 * points at them. Switching is therefore one attribute on the document
 * element; nothing in the tree has to know.
 *
 * The preference is one of three: follow the OS, or light, or dark. It is a
 * property of this browser, kept in localStorage, because a person who wants
 * their work laptop light and their bedroom tablet dark is not unusual, and
 * because it has to be readable before React mounts (index.html applies it
 * inline so the first paint is already the right color).
 *
 */

export type Theme = 'light' | 'dark';
export type ThemePreference = 'system' | 'light' | 'dark';

const THEME_KEY = 'slate.theme';

/**
 * Read by index.html before React mounts, so the first paint is already the
 * right color; keep the key and the values in step with the inline script
 * there. Anything unrecognised is "system", which is also what a first-time
 * visitor gets. localStorage is wrapped because it throws in private-mode
 * Safari, and a panel that will not open over that would be silly.
 */
export const themePreference = {
  key: THEME_KEY,
  get: (): ThemePreference => {
    try {
      const value = window.localStorage.getItem(THEME_KEY);
      return value === 'light' || value === 'dark' ? value : 'system';
    } catch {
      return 'system';
    }
  },
  set: (value: ThemePreference): void => {
    try {
      window.localStorage.setItem(THEME_KEY, value);
    } catch {
      // The choice still applies for this page; it just will not be remembered.
    }
  },
};

export const THEME_ATTRIBUTE = 'data-theme';

/** The address bar / status bar color for each theme: the page color. */
const THEME_COLOR: Record<Theme, string> = { dark: '#0B0D10', light: '#F7F8FA' };

const DARK_QUERY = '(prefers-color-scheme: dark)';

function osTheme(): Theme {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'dark';
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
}

export function resolveTheme(preference: ThemePreference, os: Theme): Theme {
  return preference === 'system' ? os : preference;
}

/** Write the theme onto the document. Idempotent; safe to call often. */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  if (root.getAttribute(THEME_ATTRIBUTE) !== theme) root.setAttribute(THEME_ATTRIBUTE, theme);
  root.style.colorScheme = theme;
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta && meta.content !== THEME_COLOR[theme]) meta.content = THEME_COLOR[theme];
}

interface ThemeContextValue {
  /** What the person asked for. */
  preference: ThemePreference;
  /** What the page is actually showing, after the OS and any pin. */
  theme: Theme;
  setPreference: (next: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => themePreference.get());
  const [os, setOs] = useState<Theme>(osTheme);

  // The OS setting, live: macOS switches at sunset and the app should too.
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia(DARK_QUERY);
    const onChange = (event: MediaQueryListEvent) => setOs(event.matches ? 'dark' : 'light');
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  // Another tab changed the preference: follow it, so two windows of the
  // same app never disagree about what time of day it is.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === themePreference.key) setPreferenceState(themePreference.get());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const theme = resolveTheme(preference, os);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setPreference = useCallback((next: ThemePreference) => {
    themePreference.set(next);
    setPreferenceState(next);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, theme, setPreference }),
    [preference, theme, setPreference],
  );

  return createElement(ThemeContext.Provider, { value }, children);
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (!value) throw new Error('useTheme must be used inside <ThemeProvider>.');
  return value;
}

